import { BarChart } from '@mui/x-charts/BarChart'
import { Typography, CircularProgress, Box, Card, CardContent, Grid } from '@mui/material'
import { useGetCarRegistrationChartQuery } from 'store/dashboard'
import HorizontalLineChart from 'components/chart/HorizontalLineChart'
import NoDataAvailable from './NoDataAvailable'
import { useTimeRange } from './TimeRangeContext'
import HorizontalBarList from './HorizontalBarList'
import { barChartSlotProps, chartSx } from './chartDefaults'

const CarRegistrationChart = () => {
  const { startDate, endDate } = useTimeRange()

  const { data, isLoading, isError, refetch, isFetching } = useGetCarRegistrationChartQuery({
    startDate,
    endDate
  })

  return (
    <Box>
      {isLoading || isFetching ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <CircularProgress />
        </Box>
      ) : isError ? (
        <NoDataAvailable variant='error' onRefresh={refetch} />
      ) : !data ? (
        <NoDataAvailable onRefresh={refetch} />
      ) : data.data.length === 0 ? (
        <NoDataAvailable onRefresh={refetch} />
      ) : (
        <>
          <Box sx={{ width: '100%', height: 300, mb: 3 }}>
            <BarChart
              xAxis={[{ scaleType: 'band', data: data?.data?.map?.((d) => d.label) || [] }]}
              yAxis={[{ tickMinStep: 1 }]}
              series={[
                {
                  label: 'Registration',
                  data: data?.data?.map?.((d) => d.registration) || [],
                  color: '#002C5F'
                },
                {
                  label: 'Approve',
                  data: data?.data?.map?.((d) => d.approved) || [],
                  color: '#4CAF50'
                },
                {
                  label: 'Reject',
                  data: data?.data?.map?.((d) => d.rejected) || [],
                  color: '#F44336'
                },
                {
                  label: 'Pending',
                  data: data?.data?.map?.((d) => d.pending) || [],
                  color: '#FF9800'
                }
              ]}
              height={300}
              slotProps={barChartSlotProps}
              sx={chartSx}
            />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 2 }}>
            <Card sx={{ border: '1px solid #EAECF0', boxShadow: 'none', bgcolor: 'white' }}>
              <CardContent sx={{ textAlign: 'center', p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                  Total Registered
                </Typography>
                <Typography variant='h5' sx={{ fontWeight: 700 }}>
                  {data.totalRegistered}
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ border: '1px solid #EAECF0', boxShadow: 'none', bgcolor: 'white' }}>
              <CardContent sx={{ textAlign: 'center', p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                  Approval Rate
                </Typography>
                <Typography variant='h5' sx={{ fontWeight: 700, color: '#4CAF50' }}>
                  {data.totalRegistered > 0
                    ? ((data.approved / data.totalRegistered) * 100).toFixed(1)
                    : 0}%
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ border: '1px solid #EAECF0', boxShadow: 'none', bgcolor: 'white' }}>
              <CardContent sx={{ textAlign: 'center', p: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                  Rejection Rate
                </Typography>
                <Typography variant='h5' sx={{ fontWeight: 700, color: '#F44336' }}>
                  {data.totalRegistered > 0
                    ? ((data.rejected / data.totalRegistered) * 100).toFixed(1)
                    : 0}%
                </Typography>
              </CardContent>
            </Card>
          </Box>

          <Box>
            <Box sx={{ p: 3, borderRadius: '5px', bgcolor: '#EBF8FF' }}>
              <Typography sx={{ mb: 1 }}>
                <span style={{ color: '#4A90D9' }}>Conversion Rate:</span>{' '}
                <strong color='black'>{data.conversionRate}%</strong>
              </Typography>
            </Box>
          </Box>

          <Grid sx={{ mt: 3 }} container>
            <Grid item xs={6} pr={2}>
              <Typography variant='h6' sx={{ mb: 2 }}>
                Member Tier
              </Typography>
              <HorizontalLineChart showValue data={data.registrationTier || []} />
            </Grid>
            <Grid item xs={6} sx={{ borderLeft: '1px solid #cccccc50' }} pl={2}>
              <Typography variant='h6'>Top Car Model</Typography>
              {data.topCarModel?.length ? (
                <HorizontalBarList
                  data={data.topCarModel
                    .map((d) => ({
                      label: d.label,
                      value: d.value,
                      percentage:
                        (d.value / data.topCarModel.reduce((a, b) => a + b.value, 0)) * 100
                    }))
                    .sort((a, b) => b.value - a.value)}
                  colors={data.topCarModel.map((d) => d.color || '#4A90D9')}
                />
              ) : (
                <NoDataAvailable />
              )}
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  )
}

export default CarRegistrationChart
